import api from './utils/axios';

export interface DeepgramGrantResponse {
  access_token?: string;
  accessToken?: string;
  token?: string;
  expires_in?: number;
  expiresIn?: number;
  data?: DeepgramGrantResponse;
}

/** Max TTL supported by Deepgram grant; long enough for live STT sessions. */
const DEFAULT_TTL_SECONDS = 3600;
const EXPIRY_BUFFER_MS = 30_000;

let cachedToken: { accessToken: string; expiresAt: number } | null = null;
let inflightRequest: Promise<string> | null = null;

function extractAccessToken(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;

  const record = data as DeepgramGrantResponse;

  if (record.data) {
    const nested = extractAccessToken(record.data);
    if (nested) return nested;
  }

  for (const key of ['access_token', 'accessToken', 'token'] as const) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

export async function getDeepgramAccessToken(
  ttlSeconds = DEFAULT_TTL_SECONDS
): Promise<string> {
  const now = Date.now();

  if (cachedToken && cachedToken.expiresAt - EXPIRY_BUFFER_MS > now) {
    return cachedToken.accessToken;
  }

  if (!inflightRequest) {
    inflightRequest = api
      .post<DeepgramGrantResponse>('/integrations/deepgram/token', {
        ttl_seconds: ttlSeconds,
      })
      .then(({ data }) => {
        const accessToken = extractAccessToken(data);

        if (!accessToken) {
          throw new Error('Deepgram access token missing from grant response');
        }

        const expiresInSeconds =
          (typeof data === 'object' &&
            data &&
            (data.expires_in ?? data.expiresIn)) ||
          ttlSeconds;

        cachedToken = {
          accessToken,
          expiresAt: Date.now() + expiresInSeconds * 1000,
        };

        return accessToken;
      })
      .finally(() => {
        inflightRequest = null;
      });
  }

  return inflightRequest;
}

export function clearDeepgramAccessTokenCache(): void {
  cachedToken = null;
}
